import * as React from "react";
import { observer } from "mobx-react";

import { useLocalization } from "@shared-ui/localization-context";

import { UitkLayoutGridItem, UitkLayoutConditionalGridSpan } from "uitk-react-layout-grid";
import { UitkIcon } from "uitk-react-icons";
import { UitkLink, UitkLinkAlign } from "uitk-react-link";
import {
  UitkMenu,
  UitkMenuTrigger,
  UitkMenuContainer,
  UitkMenuList,
  PositionType,
  MenuItemConfig,
} from "uitk-react-menu";

import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { locKeys } from "./l10n";

export enum ClassCodes {
  ECONOMY = "e",
  PREMIUM_ECONOMY = "p",
  BUSINESS_CLASS = "b",
  FIRST_CLASS = "f",
}

export interface PreferredClassOption {
  label: string;
  classCode: ClassCodes;
}

export interface PreferredClassInputProps {
  colSpan?: UitkLayoutConditionalGridSpan;
  onSelectPreferredClassCode: (classCode: ClassCodes) => void;
  wizardState: ClientWizardStateStore;
}

export const PreferredClassInput: React.FC<PreferredClassInputProps> = observer(
  ({ wizardState, colSpan, onSelectPreferredClassCode }) => {
    const { formatText } = useLocalization();
    const { flightClassCode } = wizardState.flightWizardState;
    const { ECONOMY, PREMIUM_ECONOMY, BUSINESS_CLASS, FIRST_CLASS } = ClassCodes;
    const linkRef: React.RefObject<HTMLAnchorElement> = React.createRef();
    const preferredClassOptions: PreferredClassOption[] = [
      { label: formatText(locKeys.flightPreferredClassEconomy), classCode: ECONOMY },
      { label: formatText(locKeys.flightPreferredClassPremiumEconomy), classCode: PREMIUM_ECONOMY },
      { label: formatText(locKeys.flightPreferredClassBusiness), classCode: BUSINESS_CLASS },
      { label: formatText(locKeys.flightPreferredClassFirstClass), classCode: FIRST_CLASS },
    ];
    const preferredClassLabel = formatText(locKeys.flightPreferredClassLabel);
    const currentPreferredClassLabel = preferredClassOptions.find(({ classCode }) => classCode === flightClassCode)
      ?.label;

    const [isMenuOpen, setIsMenuOpen] = React.useState(false);

    const handleMenuTriggerClick = (event: React.MouseEvent) => {
      event.preventDefault();
      setIsMenuOpen(!isMenuOpen);
    };

    const handlePreferredClassClick = (classCode: ClassCodes) => () => {
      onSelectPreferredClassCode(classCode);
      setIsMenuOpen(false);
      linkRef.current?.focus();
    };

    return (
      <UitkLayoutGridItem colSpan={colSpan}>
        <div>
          <UitkMenu id="preferred-class-input" isOpen={isMenuOpen} onTriggerClick={handleMenuTriggerClick}>
            <UitkMenuTrigger>
              <UitkLink
                id="preferred-class-input-trigger"
                iconPos={UitkLinkAlign.RIGHT}
                data-testid="preferred-class-input-trigger"
              >
                <a
                  href=""
                  role="button"
                  aria-label={`${preferredClassLabel} ${currentPreferredClassLabel}`}
                  ref={linkRef}
                >
                  {currentPreferredClassLabel}
                  <UitkIcon name="expand_more" />
                </a>
              </UitkLink>
            </UitkMenuTrigger>

            <UitkMenuContainer position={PositionType.LEFT}>
              <UitkMenuList
                items={preferredClassOptions.map(
                  ({ label, classCode }): MenuItemConfig => ({
                    attributes: {
                      role: "menuItem",
                      tabindex: 0,
                    },
                    className: "uitk-list-item",
                    label,
                    href: "#",
                    typeAnchor: true,
                    onClick: handlePreferredClassClick(classCode),
                  })
                )}
              />
            </UitkMenuContainer>
          </UitkMenu>
        </div>
      </UitkLayoutGridItem>
    );
  }
);
